import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";

export interface IBeevisionMachine {
  _id?: string;
  name?: string;
  serialNumber?: string;
  location?: string;
  apiUrl?: string;
  status?: "available" | "not_available";
  unavailableUntil?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface IStore {
  /** State */
  beevisionMachine: IBeevisionMachine | null;
  hasNextPage: boolean;
  allBeevisionMachines: IBeevisionMachine[];
  addedBeevisionMachine?: string | null;
  updatedBeevisionMachine?: string | null;
  /** Callbacks */
  getBeevisionMachines: (page?: number, limit?: number) => void;
  getBeevisionMachine: (id: string) => void;
  addBeevisionMachine: (data: IBeevisionMachine) => void;
  deleteBeevisionMachine: (id: string) => void;
  updateBeevisionMachine: (
    id: string,
    data: {
      name: string;
      location: string;
      serialNumber: string;
      apiUrl?: string;
      status?: "available" | "not_available";
      unavailableUntil?: string | null;
    }
  ) => void;
  clearAddedBeevisionMachine: () => void;
  clearUpdatedBeevisionMachine: () => void;
}

export const useBeevisionMachines = create<IStore>()((set) => ({
  /** State */
  allBeevisionMachines: [],
  hasNextPage: false,
  beevisionMachine: null,
  addedBeevisionMachine: null,
  updatedBeevisionMachine: null,
  /** Callbacks */
  getBeevisionMachines: async (page = 1, limit = 20) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(
        `beevision-machines?page=${page}&limit=${limit}`
      );
      set((state) => ({
        ...state,
        hasNextPage: response?.data?.length === limit,
        allBeevisionMachines:
          page === 1
            ? response?.data
            : [...state.allBeevisionMachines, ...response?.data],
      }));
    } catch (error) {
      LOG.error("Error on fetching Beevision Machines", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getBeevisionMachine: async (id) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`beevision-machines/${id}`);
      set({ beevisionMachine: response.data });
    } catch (error) {
      LOG.error("Error on fetching Beevision Machine", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  addBeevisionMachine: async (body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("beevision-machines", body);
      if (response?.data) {
        const newBeevisionMachine = response.data;
        set((state) => ({
          allBeevisionMachines: [
            newBeevisionMachine,
            ...state.allBeevisionMachines,
          ],
          addedBeevisionMachine: newBeevisionMachine._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on adding new Beevision Machine", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteBeevisionMachine: async (sProductId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      await axios.delete(`beevision-machines/${sProductId}`);
      set((state) => ({
        allBeevisionMachines: state.allBeevisionMachines.filter(
          (sProduct) => sProduct._id !== sProductId
        ),
      }));
    } catch (error) {
      LOG.error("Error on deleting a Beevision Machine", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  updateBeevisionMachine: async (sProductId, body) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(
        `beevision-machines/${sProductId}`,
        body
      );
      if (response?.data) {
        const updatedSProduct = response.data;
        set((state) => ({
          allBeevisionMachines: state.allBeevisionMachines.map((sProduct) => {
            if (sProduct._id === updatedSProduct._id) {
              return updatedSProduct;
            }
            return sProduct;
          }),
          updatedBeevisionMachine: updatedSProduct._id,
        }));
      }
    } catch (error) {
      LOG.error("Error on updating a Beevision Machine", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  clearAddedBeevisionMachine: () => {
    set({ addedBeevisionMachine: null });
  },
  clearUpdatedBeevisionMachine: () => {
    set({ updatedBeevisionMachine: null });
  },
}));
